/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';

import ChallengeEntry from 'app/components/challenges/ChallengeEntry';
import Spinner from 'app/components/commons/Spinner';
import {
  getMyChallengeEntries,
  startMyChallengeEntriesPoll,
  stopMyChallengeEntriesPoll
} from 'app/actions/myChallengesActions';
import challengeEntryListPositionSelector from 'app/selectors/challengeEntryListPositionSelector';

type Props = {
  challengeId: string,
  entries?: Array<*>,
  errorMessages?: Array<string>,
  expandType: 'edit' | 'summary' | 'view',
  isLoading?: boolean,
  isRacing: boolean,
  isRapidfire: boolean,
  positions: Object,
  onFetch: () => void,
  startChallengeEntriesPoll: () => void,
  stopChallengeEntriesPoll: () => void
};

const Wrapper = styled.div(({ theme }) => ({
  color: theme.color.basis2
}));

const LoadingZone = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis6,
  borderColor: 'white',
  borderStyle: 'solid',
  borderWidth: '0 0 1px',
  padding: '1rem 2rem'
}));

const ChallengeEntryList: React.FC<Props> = ({
  onFetch,
  startChallengeEntriesPoll,
  stopChallengeEntriesPoll,
  challengeId,
  entries,
  errorMessages,
  expandType,
  isLoading,
  isRacing,
  isRapidfire,
  positions
}: Props) => {
  const init = () => {
    onFetch();
    startChallengeEntriesPoll();

    return stopChallengeEntriesPoll;
  };

  React.useEffect(init, []);

  if (entries.length === 0 && !isLoading && errorMessages.length > 0) {
    return <span>{errorMessages[0]}</span>;
  }

  if (entries.length === 0) {
    return (
      <LoadingZone>
        <Spinner center />
      </LoadingZone>
    );
  }

  return (
    <Wrapper>
      {entries.map(entry => (
        <ChallengeEntry
          key={entry.id}
          challengeId={challengeId}
          entryId={entry.id}
          expandType={expandType}
          isRacing={isRacing}
          positions={positions}
          rank={isRapidfire ? undefined : entry.rank}
          selections={entry.selections}
          score={isRapidfire ? undefined : entry.score}
          winnings={entry.winnings}
        />
      ))}
    </Wrapper>
  );
};

ChallengeEntryList.defaultProps = {
  entries: [],
  errorMessages: [],
  isLoading: false
};

const connector = connect(
  (state, { challengeId }) => {
    const entryObject = state.myChallenges.entries[challengeId] || {};
    const positions = challengeEntryListPositionSelector(state, challengeId);

    return {
      entries: entryObject.items,
      errorMessages: entryObject.errorMessages,
      isLoading: entryObject.isLoading,
      positions
    };
  },
  (dispatch, { challengeId }) => ({
    onFetch() {
      dispatch(getMyChallengeEntries(challengeId));
    },
    startChallengeEntriesPoll() {
      dispatch(startMyChallengeEntriesPoll(challengeId));
    },
    stopChallengeEntriesPoll() {
      dispatch(stopMyChallengeEntriesPoll());
    }
  })
);

export default connector(ChallengeEntryList);
